import { Preferences } from "@capacitor/preferences";
import { Network } from "@capacitor/network";
import { toast } from "sonner";

// Inspection writes that are safe to replay later. Everything else needs a live connection.
const QUEUEABLE = [
  /^\/inspection\/[^/]+\/step\/\d+\/(save|status|accept|lock)$/,
  /^\/photos\/analyze$/,
];
const KEY = "fpq_offline_queue_v1";
const listeners = new Set();
let api = null;
let online = typeof navigator === "undefined" ? true : navigator.onLine;
let pending = 0;
let flushing = false;

const emit = () => listeners.forEach((fn) => fn({ online, pending }));

export const subscribeSync = (fn) => {
  listeners.add(fn);
  fn({ online, pending });
  return () => listeners.delete(fn);
};

const read = async () => JSON.parse((await Preferences.get({ key: KEY })).value || "[]");
const write = async (q) => {
  await Preferences.set({ key: KEY, value: JSON.stringify(q) });
  pending = q.length;
  emit();
};

const isQueueable = (c) =>
  ["post", "put", "patch"].includes((c.method || "").toLowerCase()) && QUEUEABLE.some((r) => r.test(c.url || ""));

const fileToDataUrl = (f) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(f);
});

async function serialize(data) {
  if (data instanceof FormData) {
    const form = [];
    for (const [k, v] of data.entries()) {
      form.push(v instanceof Blob ? { k, file: await fileToDataUrl(v), name: v.name || "upload.jpg", type: v.type } : { k, v });
    }
    return { form };
  }
  return { json: typeof data === "string" ? JSON.parse(data || "null") : data ?? null };
}

async function deserialize(body) {
  if (!body.form) return body.json;
  const fd = new FormData();
  for (const e of body.form) {
    if (e.file) fd.append(e.k, new File([await (await fetch(e.file)).blob()], e.name, { type: e.type }));
    else fd.append(e.k, e.v);
  }
  return fd;
}

const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);

async function enqueue(config) {
  const q = await read();
  q.push({ id: config.headers["Idempotency-Key"] || newKey(), method: config.method, url: config.url,
           body: await serialize(config.data), queued_at: new Date().toISOString() });
  await write(q);
  return { data: { queued: true }, status: 202, statusText: "Queued offline", headers: {}, config };
}

export async function flushQueue() {
  if (flushing || !online || !api) return;
  flushing = true;
  try {
    let q = await read();
    while (q.length) {
      const item = q[0];
      try {
        await api.request({ method: item.method, url: item.url, data: await deserialize(item.body), _replay: true,
                            timeout: 60000, headers: { "Idempotency-Key": item.id,
                              ...(item.body.form ? { "Content-Type": "multipart/form-data" } : {}) } });
      } catch (e) {
        if (!e.response || e.response.status === 401) break; // still offline or signed out: keep and retry later
        toast.error(`A change saved offline could not sync: ${e.response.data?.detail || e.response.status}`);
      }
      q = (await read()).filter((i) => i.id !== item.id);
      await write(q);
    }
  } finally { flushing = false; }
}

export function installOfflineQueue(instance) {
  api = instance;
  instance.interceptors.request.use((config) => {
    if (isQueueable(config) && !config._replay) {
      // Same key on the first try and on every replay, so the server saves the write once.
      const key = config.headers["Idempotency-Key"] || newKey();
      config.headers["Idempotency-Key"] = key;
      if (config.data instanceof FormData && !config.data.has("client_id")) config.data.append("client_id", key);
    }
    if (!online && !config._replay && isQueueable(config)) config.adapter = () => enqueue(config);
    return config;
  });
  instance.interceptors.response.use((r) => r, (error) => {
    const c = error.config;
    if (c && !c._replay && !error.response && error.code === "ERR_NETWORK" && isQueueable(c)) return enqueue(c);
    return Promise.reject(error);
  });
  (async () => {
    pending = (await read()).length;
    online = (await Network.getStatus()).connected;
    emit();
    Network.addListener("networkStatusChange", (s) => {
      online = s.connected;
      emit();
      if (online) flushQueue();
    });
    flushQueue();
  })();
}
